// Iframe → host bridge. Two messages only, both PHI-free:
//   1. { type: 'expy:height', height } — so the host can size the iframe to content.
//   2. { type: 'expy:event', event }   — coarse conversion signals for the host's own
//      analytics (e.g. 'questionnaire_complete', 'account_created'). Names only, no data.
//
// The host page (Elementor) never receives answers. This is the structural half of the
// "no PHI in WordPress" boundary — the other half is that the widget talks to our API
// directly. loader.js listens for these. When not framed (canonical hosted route),
// window.parent === window and these are harmless no-ops.
import type { FunnelEvent } from './analytics';

const isFramed = () => {
  try {
    return window.parent && window.parent !== window;
  } catch {
    return true; // cross-origin access threw → we are framed
  }
};

export function postHeight(height: number): void {
  if (!isFramed()) return;
  window.parent.postMessage({ type: 'expy:height', height: Math.ceil(height) }, '*');
}

export function postEvent(event: FunnelEvent): void {
  if (!isFramed()) return;
  window.parent.postMessage({ type: 'expy:event', event }, '*');
}

// Observe document height and stream it to the host. Returns a cleanup fn.
export function observeHeight(): () => void {
  if (!isFramed()) return () => {};
  const emit = () => postHeight(document.documentElement.scrollHeight);
  emit();
  const ro = new ResizeObserver(emit);
  ro.observe(document.documentElement);
  window.addEventListener('load', emit);
  return () => {
    ro.disconnect();
    window.removeEventListener('load', emit);
  };
}
